import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { H3, HR } from '../Base';

import PlusSVG from '../../assets/svgs/plus.svg';

const AccordionContainer = styled.div`
  width: 100%;
  background: ${p => p.theme.colours.white};
`;

const AccordionPanel = styled.div`
  padding: ${p => p.theme.spacing(3)} ${p => p.theme.spacing(2)} 0;

  ${p => p.theme.media.md`
  padding: ${p => p.theme.spacing(5)} ${p => p.theme.spacing(2)} 0;  
  `};
`;

const activeTransit = 'opacity 0.3s, max-height 1s, padding 0.2s ease';
const inactiveTransit = 'opacity 0.2s, max-height 0.6s, padding 0.6s ease';

const AccordionContent = styled.div`
  padding: ${p =>
    p.active
      ? `${p.theme.spacing(3)} 0 ${p.theme.spacing(5)};`
      : `${p.theme.spacing(1.5)} 0`};
  max-height: ${p => (p.active ? 'auto' : 0)};
  opacity: ${p => (p.active ? '1' : 0)};
  transition: ${p => (p.active ? activeTransit : inactiveTransit)};
  overflow: hidden;

  ${p => p.theme.media.md`
    padding: ${p =>
      p.active
        ? `${p.theme.spacing(5)} 0 ${p.theme.spacing(9)};`
        : `${p.theme.spacing(2.5)} 0`};
  `};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const IconContainer = styled.div`
  width: ${p => p.theme.spacing(2)};
  height: ${p => p.theme.spacing(2)};

  ${p => p.theme.media.md`
    width: ${p => p.theme.spacing(4)};
    height: ${p => p.theme.spacing(4)};
  `};
`;

const PlusIcon = styled(PlusSVG)`
  width: 100%;
  height: 100%;
  transform: rotate(${p => (p.active ? '45deg' : '0deg')});
  transition: transform 0.1s linear;
`;

const StyledHR = styled(HR)`
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
`;

const Accordion = ({ defaultActiveIndex, panels }) => {
  const [activeIndexes, setActiveIndexes] = useState([defaultActiveIndex]);

  const handleTitleClick = index => {
    if (activeIndexes.includes(index)) {
      setActiveIndexes(
        activeIndexes.filter(activeIndex => activeIndex !== index)
      );
    } else {
      setActiveIndexes([...activeIndexes, index]);
    }
  };

  return (
    <AccordionContainer>
      {panels.map((panel, panelIndex) => {
        const active = activeIndexes.includes(panelIndex);

        return (
          <Fragment key={panel.key}>
            <StyledHR />
            <AccordionPanel>
              <TitleRow
                data-testid={`title-${panel.key}`}
                onClick={() => handleTitleClick(panelIndex)}
              >
                <H3>{panel.title}</H3>
                <IconContainer>
                  <PlusIcon active={active ? 1 : 0} />
                </IconContainer>
              </TitleRow>
              <AccordionContent
                data-testid={`${panel.key}-${active}`}
                active={active}
              >
                {panel.content.map((content, index) => {
                  return <Fragment key={index}>{content}</Fragment>;
                })}
              </AccordionContent>
            </AccordionPanel>
            {panelIndex === panels.length - 1 && <StyledHR />}
          </Fragment>
        );
      })}
    </AccordionContainer>
  );
};

Accordion.defaultProps = {};

Accordion.propTypes = {
  defaultActiveIndex: PropTypes.number,
  panels: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.string,
      content: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.element])
      )
    })
  )
};

export default Accordion;
